import { PopoverVirtualElement } from '@mui/material';
import { EndpointUrl } from 'api/endpoint';
import { useEntityActions } from 'api/entity';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { animate, useMotionValue } from 'framer-motion';
import { useClipboard } from 'providers/ClipboardProvider';
import { useDraggable } from 'providers/DnDProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { EntityViewProps } from './index.types';

export function useEntityViewController(props: EntityViewProps) {
  const {
    entity,
    viewType,
    hideType,
    hideParent,
    parentControlledCardSize,
    disableActions,
  } = props;

  const [searchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | PopoverVirtualElement>(null);
  const [dragging, setDragging] = useState(false);

  const draggableContext = useDraggable();
  const workspaceId = useWorkspaceId();
  const { updateEntity } = useEntityActions();
  const { mutate } = useSWRConfig();
  const { isInClipboard, addToClipboard, copiedEntities, removeFromClipboard } =
    useClipboard();

  const isSelected = useMemo(
    () => entity && isInClipboard(entity.id),
    [entity, isInClipboard],
  );

  const onMouseMove = useRef<((e: MouseEvent) => void) | null>(null);
  const onMouseUp = useRef<((e: MouseEvent) => void) | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const elemRef = useRef<HTMLDivElement | null>(null);
  const origMouseOffsetX = useMotionValue(0);
  const origMouseOffsetY = useMotionValue(0);
  const mouseCoords = useRef({ x: 0, y: 0 });

  useEffect(() => {
    return () => {
      if (onMouseMove.current) {
        document.removeEventListener('mousemove', onMouseMove.current);
      }
      if (onMouseUp.current) {
        document.removeEventListener('mouseup', onMouseUp.current);
      }
    };
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    if (disableActions || !entity) return;
    setDragging(true);

    draggableContext.setDragItem({
      type: 'entity',
      data: entity,
    });

    const elemCoords = elemRef.current!.getBoundingClientRect();
    origMouseOffsetX.set(e.clientX - elemCoords.x);
    origMouseOffsetY.set(e.clientY - elemCoords.y);

    const animateHandlerX = origMouseOffsetX.on('change', (val) => {
      popperRef.current!.style.left = `${mouseCoords.current.x - val}px`;
    });

    const animateHandlerY = origMouseOffsetY.on('change', (val) => {
      popperRef.current!.style.top = `${mouseCoords.current.y - val}px`;
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onMouseMove.current = (e: MouseEvent) => {
      mouseCoords.current = { x: e.clientX, y: e.clientY };
      if (popperRef.current) {
        popperRef.current.style.left = `${e.clientX - origMouseOffsetX.get()}px`;
        popperRef.current.style.top = `${e.clientY - origMouseOffsetY.get()}px`;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onMouseUp.current = (e: MouseEvent) => {
      setDragging(false);
      draggableContext.setDragItem(null);
      animateHandlerX();
      animateHandlerY();
      window.removeEventListener('mousemove', onMouseMove.current!);
      window.removeEventListener('mouseup', onMouseUp.current!);
    };

    window.addEventListener('mousemove', onMouseMove.current);
    window.addEventListener('mouseup', onMouseUp.current);

    animate(origMouseOffsetX, 0, {
      // duration: 0.5,
      type: 'spring',
      stiffness: 100,
      damping: 15,
      velocity: 0,
    });
    animate(origMouseOffsetY, 0, {
      // duration: 0.5,
      type: 'spring',
      stiffness: 100,
      damping: 15,
      velocity: 0,
    });
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (disableActions) return;
    e.preventDefault();
    setAnchorEl({
      nodeType: 1,
      getBoundingClientRect: () => {
        return {
          x: e.clientX,
          y: e.clientY,
          top: e.clientY,
          left: e.clientX,
          right: e.clientX,
          bottom: e.clientY,
          height: 0,
          width: 0,
          toJSON: () => '',
        };
      },
    });
  };

  const handleMoreButtonClick = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setAnchorEl({
      nodeType: 1,
      getBoundingClientRect: () => rect,
    });
  };

  const handleListItemClick = (e: React.MouseEvent) => {
    if (!entity) return;

    // If ctrl+shift key is pressed, open in new tab
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && !disableActions) {
      e.preventDefault();
      const entityUrl = `/workspace/${workspaceId}/entity/${entity.id}`;
      window.open(entityUrl, '_blank');
      return;
    }
    // If ctrl key is pressed, add to clipboard and prevent navigation
    if ((e.ctrlKey || e.metaKey) && !disableActions) {
      e.preventDefault();
      if (isInClipboard(entity.id)) {
        removeFromClipboard(entity.id);
      } else if ('description' in entity) {
        addToClipboard(entity);
      }
    }
  };

  const handleFieldChange = async (fieldName: string, value: unknown) => {
    if (!entity || !('description' in entity)) return;

    await updateEntity(entity.id, {
      extraFields: { [fieldName]: value },
    });
    mutate(
      `${EndpointUrl}/workspace/${workspaceId}/entity/${entity.parent}/children?all=false`,
    );
  };

  return {
    entity,
    viewType,
    hideType,
    hideParent,
    parentControlledCardSize,
    disableActions,
    searchParams,
    anchorEl,
    dragging,
    isSelected,
    workspaceId,
    copiedEntities,
    elemRef,
    popperRef,
    handleDrag,
    handleContextMenu,
    handleMoreButtonClick,
    handleListItemClick,

    handleFieldChange,
    setAnchorEl,
    isInClipboard,
  };
}
